import React from 'react';
import { useFormikContext } from 'formik';

import MultiSelectMenu from '@dashboard/components/MultiSelectMenu';
import { Select } from '@dashboard/components/Formik';

import { getPriceSelectLabel } from '../PricesList/priceHelpers';
import { ORDER_BUMP_COPY } from './offerCopy';

export const ORDER_BUMP_SUBJECT = 'ORDER_BUMP';

export const getSelectionSubject = ticketTypes => (Array.isArray(ticketTypes) ? 'TICKET' : 'PRICE');

/**
 * The bump's visibility rule, read top to bottom as a sentence like the variant
 * version (ShowHideLogicField): subject, comparison, value. The subject is what
 * the page sells (its price on a checkout, its tickets on an event) or the
 * page's other bumps, which a variant rule's "Variant" select is the model for.
 *
 * Writes to Formik only. The rule belongs to the placement, so it is saved by
 * the form's submit handler rather than committing as it is edited the way
 * Preselect and Hide do: a half-changed sentence ("is not" still pointing at
 * the old price) would otherwise move the bump in and out of the preview while
 * the seller was composing it.
 */
export default function BumpConditionalLogicField({
  prices = [],
  ticketTypes,
  otherOrderBumps = [],
  copy = ORDER_BUMP_COPY,
}) {
  const { values, setFieldValue } = useFormikContext();

  const logic = values.showHideLogic ?? {};
  const selectionSubject = getSelectionSubject(ticketTypes);
  const subject = logic.subject === ORDER_BUMP_SUBJECT ? ORDER_BUMP_SUBJECT : selectionSubject;
  const enabledPrices = prices.filter(price => price?.enabled !== false);
  const selectedPriceIds = (logic.priceIds ?? []).map(String);
  // With one price the buyer has no choice to watch, so the list offers nothing,
  // except a price a rule already names, which stays so it can be read and changed.
  const priceOptions =
    enabledPrices.length > 1
      ? enabledPrices
      : enabledPrices.filter(price => selectedPriceIds.includes(String(price._id)));
  const offeredPriceIds = new Set(priceOptions.map(price => String(price._id)));
  // A ticked price the page no longer offers stays listed, or the seller could
  // never untick it and the rule would hide the bump on every price for good.
  const unofferedPriceItems = selectedPriceIds
    .filter(id => !offeredPriceIds.has(id))
    .map(id => {
      const price = prices.find(candidate => String(candidate._id) === id);
      if (price) return { id, label: getPriceSelectLabel(price), detail: 'Disabled' };
      const savedName = (logic.labels ?? []).find(label => String(label.id) === id)?.name;
      return savedName
        ? { id, label: savedName, detail: 'Deleted' }
        : { id, label: 'Deleted price' };
    });
  const selectedTicketTypeIds = (logic.ticketTypeIds ?? []).map(String);
  const selectedOrderBumpIds = (logic.orderBumpProductIds ?? []).map(String);

  // Written whole rather than per leaf, so the comparison and its ids always
  // land together.
  const update = patch =>
    setFieldValue('showHideLogic', {
      enabled: true,
      subject,
      comparison: logic.comparison || 'IS',
      priceIds: selectedPriceIds,
      ticketTypeIds: selectedTicketTypeIds,
      orderBumpProductIds: selectedOrderBumpIds,
      labels: logic.labels,
      ...patch,
    });

  // The value a rule names only means anything against its own subject, so a
  // switch starts the value over rather than carrying a stale one along.
  const changeSubject = nextSubject =>
    update({ subject: nextSubject, priceIds: [], ticketTypeIds: [], orderBumpProductIds: [] });

  const toggle = (key, selectedIds, id) =>
    update({
      [key]: selectedIds.includes(id)
        ? selectedIds.filter(candidate => candidate !== id)
        : [...selectedIds, id],
    });

  let value;
  if (subject === ORDER_BUMP_SUBJECT) {
    value = (
      <MultiSelectMenu
        id="showHideLogic.orderBumpProductIds"
        placeholder={`Select ${copy.plural}`}
        emptyText={`This page has no other ${copy.plural} yet.`}
        items={otherOrderBumps.map(bump => ({ id: String(bump.productId), label: bump.title }))}
        selectedIds={selectedOrderBumpIds}
        onToggle={id => toggle('orderBumpProductIds', selectedOrderBumpIds, id)}
      />
    );
  } else if (subject === 'TICKET') {
    value = (
      <MultiSelectMenu
        id="showHideLogic.ticketTypeIds"
        placeholder="Select tickets"
        emptyText="This event has no tickets yet."
        items={ticketTypes.map(ticketType => ({
          id: String(ticketType._id),
          label: ticketType.name,
          detail: ticketType.groupName,
        }))}
        selectedIds={selectedTicketTypeIds}
        onToggle={id => toggle('ticketTypeIds', selectedTicketTypeIds, id)}
      />
    );
  } else {
    value = (
      <MultiSelectMenu
        id="showHideLogic.priceIds"
        placeholder="Select prices"
        emptyText={
          enabledPrices.length ? 'This page has only one price.' : 'This page has no prices yet.'
        }
        items={[
          ...priceOptions.map(price => ({
            id: String(price._id),
            label: getPriceSelectLabel(price),
          })),
          ...unofferedPriceItems,
        ]}
        selectedIds={selectedPriceIds}
        onToggle={id => toggle('priceIds', selectedPriceIds, id)}
      />
    );
  }

  return (
    <div className="grid grid-cols-[8rem_1fr] items-center gap-x-2 gap-y-2">
      <Select
        id="showHideLogic.subject"
        name="showHideLogic.subject"
        value={subject}
        onChange={event => changeSubject(event.target.value)}
      >
        <option value={selectionSubject}>
          {selectionSubject === 'TICKET' ? 'Ticket' : 'Price'}
        </option>
        <option value={ORDER_BUMP_SUBJECT}>{copy.Noun}</option>
      </Select>

      <Select
        id="showHideLogic.comparison"
        name="showHideLogic.comparison"
        className="w-32"
        value={logic.comparison || 'IS'}
        onChange={event => update({ comparison: event.target.value })}
      >
        <option value="IS">is</option>
        <option value="IS_NOT">is not</option>
      </Select>

      {/* The full row, so a product title is not clipped to the comparison's width. */}
      <div className="col-span-2">{value}</div>
    </div>
  );
}
